import { useParams, Link } from "react-router-dom";
import { useMemo, useEffect, useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { getCategories, getProducts } from "../../api/apiServices";
import { useProductModal } from "../../context/ProductModalContext";
import { slugify } from "../../utils/slugify";

export default function ProductsPage() {
  const { category: categorySlug } = useParams();
  const { openProductModal } = useProductModal();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [productsRes, categoriesRes] = await Promise.all([
          getProducts(),
          getCategories(),
        ]);

        setProducts(productsRes);
        setCategories(categoriesRes.data || []);
      } catch (err) {
        console.error("Failed to load shop data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // ── Group products by category name ────────────────────────────────────────
  const productsByCategory = useMemo(() => {
    const map = {};

    products.forEach((product) => {
      const catName = product.category?.name || "Uncategorized";
      if (!map[catName]) map[catName] = [];
      map[catName].push(product);
    });

    return Object.entries(map)
      .map(([name, items]) => ({ name, items }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  // ── Determine what to show ─────────────────────────────────────────────────
  const displayedGroups = useMemo(() => {
    if (!categorySlug || categorySlug === "all") {
      return productsByCategory;
    }

    const normalizedSlug = categorySlug.toLowerCase();
    const matchingGroup = productsByCategory.find(
      (group) => slugify(group.name) === normalizedSlug
    );

    return matchingGroup ? [matchingGroup] : [];
  }, [productsByCategory, categorySlug]);

  // For hero title & active pill highlighting
  const currentCategory = categories.find(
    (cat) => slugify(cat.name) === (categorySlug || "").toLowerCase()
  );

  const pageTitle =
    categorySlug && categorySlug !== "all"
      ? currentCategory?.name || "Loading Category..."
      : "All Products";

  const formatPrice = (price) => `₦${Number(price).toLocaleString()}`;

  const getDiscount = (original, current) =>
    original > current ? Math.round(((original - current) / original) * 100) : 0;

  // ── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-white font-['Poppins']">
      <Navbar />

      {/* HERO */}
      <div className="relative h-[280px] md:h-[320px] bg-gradient-to-r from-[#EF579A] to-[#f78bb8] overflow-hidden">
        <div className="relative h-full max-w-[1200px] mx-auto px-4 flex flex-col justify-center items-center text-center pt-16">
          <nav className="flex items-center gap-2 text-white/80 text-sm mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white font-medium">{pageTitle}</span>
          </nav>

          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-white mb-3">
            {pageTitle || "Loading"}
          </h1>

          <p className="text-white/90 text-sm md:text-base max-w-md">
            Discover our beautiful collection
            {pageTitle !== "All Products" ? ` of ${pageTitle.toLowerCase()}` : ""}
          </p>
        </div>
      </div>

      <main className="max-w-[1200px] mx-auto px-4 py-10">

        {/* CATEGORY PILLS */}
        <div className="flex flex-wrap gap-3 mb-10 justify-center">
          <Link
            to="/products/all"
            className={`px-5 py-2 rounded-full text-sm font-medium ${
              !categorySlug || categorySlug === "all"
                ? "bg-[#EF579A] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-pink-50 hover:text-[#EF579A]"
            }`}
          >
            All
          </Link>

          {categories
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((cat) => {
              const isActive = categorySlug === slugify(cat.name);
              return (
                <Link
                  key={cat._id}
                  to={`/products/${slugify(cat.name)}`}
                  className={`px-5 py-2 rounded-full text-sm font-medium ${
                    isActive
                      ? "bg-[#EF579A] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-pink-50 hover:text-[#EF579A]"
                  }`}
                >
                  {cat.name}
                </Link>
              );
            })}
        </div>

        {loading ? (
          <div className="text-center py-20">Loading products...</div>
        ) : displayedGroups.length === 0 ? (
          <div className="text-center py-20">
            {categorySlug && categorySlug !== "all"
              ? "No products found in this category"
              : "No products available"}
          </div>
        ) : (
          <div className="space-y-16">
            {displayedGroups.map(({ name, items }) => (
              <section key={name} id={slugify(name)}>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
                  {name}
                </h2>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {items.map((product) => {
                    const discount =
                      product.originalPrice > product.price
                        ? getDiscount(product.originalPrice, product.price)
                        : 0;

                    return (
                      <div
                        key={product._id}
                        onClick={() =>
                          openProductModal({
                            id: product._id,
                            name: product.name,
                            price: product.price,
                            originalPrice: product.originalPrice,
                            image: product.images?.[0],
                            badges: product.badge ? [product.badge] : [],
                            description: product.description,
                          })
                        }
                        className="group bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 border border-gray-100"
                      >
                        <div className="relative aspect-[4/5] overflow-hidden bg-gray-50">
                          <img
                            src={product.images?.[0]}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />

                          {product.badge && (
                            <span className="absolute top-3 left-3 bg-[#EF579A] text-white text-[10px] px-2 py-1 rounded-full">
                              {product.badge}
                            </span>
                          )}

                          {discount > 0 && (
                            <span className="absolute top-3 right-3 bg-red-500 text-white text-[10px] px-2 py-1 rounded-full">
                              -{discount}%
                            </span>
                          )}
                        </div>

                        <div className="p-4">
                          <h3 className="font-medium text-gray-800 text-sm mb-2 line-clamp-1">
                            {product.name}
                          </h3>

                          <div className="flex items-center gap-2">
                            <span className="text-[#EF579A] font-bold text-base">
                              {formatPrice(product.price)}
                            </span>
                            {product.originalPrice > product.price && (
                              <span className="text-gray-400 text-xs line-through">
                                {formatPrice(product.originalPrice)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}